// src/features/patient/care-treatment/components/CarePlansTeamTabs.tsx

/**
 * Purpose : The Care plans and Care team tabs. Care plans: only real plans, in the existing plan card look, with
 * their goals and activities shown and empty fields hidden. Care team: the
 * people who saw the patient or wrote notes, grouped Doctors / Nurses &
 * medical assistants / Other; each card opens the timeline filtered to them.
 *
 * Contents :
 *
 * * function(CarePlansTab) : the plan cards
 * * function(CareTeamTab) : the grouped team
 */

import { Icon } from "@iconify/react";
import { EmptyState } from "@/shared/components/EmptyState";
import { cn } from "@/shared/lib/utils";
import type { CarePlanOut, CareTeamMember } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { formatDateOnly } from "@/features/patient/care-treatment/utils/careTimeline";
import { groupCareTeam, initials } from "@/features/patient/care-treatment/utils/careTeamPlans";
import {
  cardClass,
  clickableCardClass,
  concernLabelClass,
  labelTextClass,
  mutedIconClass,
  redIconBoxClass,
  subTextClass,
  textLinkClass,
} from "./careStyles";
import { LoadOlderButton, usePaged } from "./LoadOlder";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// function(CarePlansTab)
/**
 * @param plans - Real care plans only (already cleaned).
 * @param visitLabel - The visit a plan was made at, as "Prenatal visit · May 27, 2016" (null: not shown).
 * @param onOpenVisit - Open that visit.
 */
export function CarePlansTab({ plans: allPlans, visitLabel, onOpenVisit }: {
  plans: CarePlanOut[];
  visitLabel?: (encounterId: string) => string | null;
  onOpenVisit?: (encounterId: string) => void;
}) {
  const paged = usePaged(allPlans);
  const plans = paged.shown;
  if (allPlans.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:clipboard-list" className={mutedIconClass} />}
        title="No care plans"
        description="Care plans from your care team will appear here"
      />
    );
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-2">
        {plans.map((plan) => {
          const byline = [plan.author, formatDateOnly(plan.date)].filter(Boolean).join(" · ");
          return (
            <section key={plan.id} aria-label={plan.title} className={cardClass}>
              <div className="flex items-start gap-4">
                <div className={cn(redIconBoxClass, "h-12 w-12")}>
                  <Icon icon="lucide:clipboard-list" className="h-6 w-6 !text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="!mb-0 font-extrabold text-[#0A2947]">{plan.title}</h3>
                    {plan.status && <span className={cn("shrink-0", concernLabelClass)}>{capitalise(plan.status)}</span>}
                  </div>
                  {byline && <p className="!mb-0 mt-1 text-sm text-muted-foreground">{byline}</p>}
                  {plan.goals.length > 0 && (
                    <div className="mt-3">
                      <p className={cn("!mb-1", labelTextClass)}>Goals</p>
                      <ul className="!mb-0 list-disc space-y-1 pl-5 text-sm text-[#111827]">
                        {plan.goals.map((goal, i) => <li key={i}>{goal}</li>)}
                      </ul>
                    </div>
                  )}
                  {plan.activities.length > 0 && (
                    <div className="mt-3">
                      <p className={cn("!mb-1", labelTextClass)}>Activities</p>
                      <ul className="!mb-0 list-disc space-y-1 pl-5 text-sm text-[#111827]">
                        {plan.activities.map((activity, i) => <li key={i}>{activity}</li>)}
                      </ul>
                    </div>
                  )}
                  {plan.encounterId && visitLabel?.(plan.encounterId) && (
                    <button
                      type="button"
                      onClick={() => onOpenVisit?.(plan.encounterId as string)}
                      className={cn("mt-3 inline-flex items-center gap-1.5 text-left", textLinkClass)}
                    >
                      <Icon icon="lucide:calendar-days" className="h-4 w-4 shrink-0" aria-hidden="true" />
                      Made at: {visitLabel(plan.encounterId)}
                    </button>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>
      <LoadOlderButton show={paged.hasMore} onClick={paged.more} label="Show more" />
    </div>
  );
}

// function(CareTeamTab)
/**
 * @param members - The team (from the server, or built from visits and notes).
 * @param onPick - Show the timeline filtered to this person.
 */
export function CareTeamTab({ members, onPick }: { members: CareTeamMember[]; onPick: (member: CareTeamMember) => void }) {
  // 10 people at a time, in the groups' order (doctors first), regrouped.
  const paged = usePaged(groupCareTeam(members).flatMap((g) => g.members));
  const groups = groupCareTeam(paged.shown);

  return (
    <div className="grid gap-4">
      <p className={cn("!mb-0 px-1", subTextClass)}>
        The people who saw you or wrote notes about your care. Choose someone to see their visits.
      </p>

      {groups.length === 0 ? (
        <EmptyState
          icon={<Icon icon="lucide:users" className={mutedIconClass} />}
          title="No care team yet"
          description="The people who see you or write notes about your care will appear here"
        />
      ) : (
        groups.map((group) => (
          <section key={group.label} aria-label={group.label} className="grid gap-2">
            <h2 className="!mb-0 px-1 text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">{group.label}</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {group.members.map((member) => {
                const where = [member.departments.join(", "), member.organisation].filter(Boolean).join(" · ");
                const stats = [
                  member.notes > 0 && plural(member.notes, "note", "notes"),
                  member.lastSeen && `last seen ${formatDateOnly(member.lastSeen)}`,
                ].filter(Boolean).join(" · ");
                return (
                  <button
                    key={member.key}
                    type="button"
                    onClick={() => onPick(member)}
                    aria-label={`${member.name}: see visits`}
                    className={cn("flex w-full items-start gap-3 border p-4 text-left", clickableCardClass)}
                  >
                    <span aria-hidden="true" className={cn(redIconBoxClass, "h-11 w-11 rounded-full text-sm font-bold !text-white")}>
                      {initials(member.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-extrabold text-[#0A2947]">{member.name}</span>
                      {where && <span className="block truncate text-sm text-[#6B7280]">{where}</span>}
                      {stats && <span className="mt-1 block text-xs text-[#9CA3AF]">{stats}</span>}
                    </span>
                    <Icon icon="lucide:chevron-right" className="h-5 w-5 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}
      <LoadOlderButton show={paged.hasMore} onClick={paged.more} label="Show more" />
    </div>
  );
}
